import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2, Mic, MicOff } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { useSpeech } from "../lib/useSpeech";

const STARTERS = [
  "Quels plats mettre à la carte ce soir ?",
  "Comment récupérer 500 € de marge par mois ?",
  "Quelle recette a la marge la plus fragile ?",
  "Où est ma plus grosse hausse fournisseur ?",
];

export default function AdvisorChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const scrollRef = useRef();
  const inputRef = useRef();

  const speech = useSpeech({
    lang: "fr-FR",
    continuous: true,
    interimResults: true,
    onFinal: (chunk) => setInput((prev) => (prev ? `${prev} ${chunk}` : chunk)),
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const toggleMic = () => {
    if (speech.listening) speech.stop();
    else { speech.start(); inputRef.current?.focus(); }
  };

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    if (speech.listening) speech.stop();
    setInput("");
    const history = messages.slice(-6);
    setMessages([...messages, { role: "user", text: q }]);
    setLoading(true);
    try {
      const r = await api.advisorChat({ message: q, session_id: session, history });
      setSession(r.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Désolé, je n'ai pas pu répondre. Réessayez dans un instant." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="advisor-fab"
          onClick={() => setOpen(true)}
          className="no-print fixed bottom-4 right-4 z-40 h-14 w-14 rounded-full bg-[#2878FF] hover:bg-[#1769E0] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          title="Conseiller RentaScan"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="advisor-panel"
          className="no-print fixed bottom-4 right-4 z-40 w-[92vw] sm:w-96 h-[70vh] max-h-[600px] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#2878FF] flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading font-semibold text-sm">Conseiller RentaScan</div>
                <div className="text-[11px] text-slate-400">Basé sur vos données · GPT 5.6 Terra</div>
              </div>
            </div>
            <button data-testid="advisor-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <>
                <div className="text-sm text-slate-700">
                  Bonjour&nbsp;! Je réponds aux questions sur vos marges, hausses et recettes.
                  Aucun chiffre inventé — uniquement vos données.
                </div>
                <div className="space-y-2 mt-3">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      data-testid="advisor-starter"
                      onClick={() => ask(s)}
                      className="w-full text-left text-xs px-3 py-2 rounded-md border border-slate-200 bg-white hover:border-[#B4D1F9] hover:bg-[#DCEBFF] text-slate-700"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] text-sm rounded-lg px-3 py-2 whitespace-pre-wrap ${m.role === "user" ? "bg-[#2878FF] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> réflexion…
                </div>
              </div>
            )}
          </div>

          {speech.listening && (
            <div className="px-3 py-1.5 border-t border-slate-200 bg-[#FFF7E6] text-[11.5px] text-[#B36B00] font-semibold flex items-center gap-2" data-testid="advisor-mic-status">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF9F1C] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF9F1C]" />
              </span>
              Dictée en cours… parlez, je transcris en direct.
              {speech.transcript && <span className="italic text-[#5E6875] truncate max-w-[55%]">« {speech.transcript} »</span>}
            </div>
          )}
          {speech.error === "not-allowed" && (
            <div className="px-3 py-1.5 border-t border-[#F5C7C7] bg-[#FDECEC] text-[11.5px] text-[#7C2A21]" data-testid="advisor-mic-denied">
              Micro bloqué : autorisez l'accès dans les réglages du navigateur.
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="p-3 border-t border-slate-200 bg-white flex gap-2"
          >
            <input
              ref={inputRef}
              data-testid="advisor-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={speech.listening ? "Parlez… (ou tapez)" : "Posez votre question…"}
              className="flex-1 text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-[#8AB6E6]"
            />
            {speech.supported && (
              <Button
                type="button"
                data-testid="advisor-mic"
                onClick={toggleMic}
                title={speech.listening ? "Arrêter la dictée" : "Dicter la question"}
                className={
                  speech.listening
                    ? "bg-[#FF9F1C] hover:bg-[#E88A00] text-white animate-pulse"
                    : "bg-white border border-slate-200 hover:bg-slate-50 text-[#0B1F33]"
                }
                size="icon"
              >
                {speech.listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
            )}
            <Button
              type="submit"
              data-testid="advisor-send"
              disabled={loading || !input.trim()}
              className="bg-[#2878FF] hover:bg-[#1769E0] text-white"
              size="icon"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
